import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ListCountriesQuery } from '../../../../../../../../../graphql/fragment/audiences/ListCountries.generated';
import type { ListRegionsQuery } from '../../../../../../../../../graphql/fragment/audiences/ListRegions.generated';
import type { FormSegment, LookalikeSegmentDefinition } from '../../../../../../types';
import { type DrawerTypeValue, drawerType } from '../../../../drawerTypes';
import { SegmentCategory } from '../../../../hooks/types';
import { SegmentAlert } from '../../../expandableSections/segments/SegmentAlert';
import {
  createBasicInfoPropsFromLookalike,
  createBasicInfoPropsFromSegment,
} from '../../../utils/createBasicInfoProps';
import { getSegmentExpirationAlertProps } from '../../../utils/getSegmentExpirationAlertProps';
import { isFormSegment } from '../../utils';
import { BasicInfo } from '../SegmentListItem/BasicInfo';
import { Description } from '../SegmentListItem/Description';
import { DetailedInfo } from '../SegmentListItem/DetailedInfo';
import { SegmentHeader } from '../SegmentListItem/SegmentHeader';
import { itemStyles } from '../SegmentListItem/styles';
import { useSegmentHeaderElements } from '../SegmentListItem/useSegmentHeaderElements';
import { createSegmentMetadata } from './utils/createSegmentMetadata';
import { formatReachValue } from './utils/formatReachValue';

export type SegmentListItemProps = {
  type: DrawerTypeValue;
  segment: FormSegment | LookalikeSegmentDefinition;
  isSelected: boolean;
  onSelect: () => void;
  providerName?: string;
  organizationName?: string;
  searchPhrase?: string;
  dataTest?: string;
  selectedAccountId: string;
  regions: ListRegionsQuery['region'];
  countries: ListCountriesQuery['country'];
};
export const SegmentListItem = ({
  type,
  segment,
  isSelected,
  onSelect,
  providerName,
  organizationName,
  searchPhrase,
  dataTest = type === drawerType.LOOKALIKE ? 'lookalike-item' : 'segments-item',
  selectedAccountId,
  regions,
  countries,
}: SegmentListItemProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const isLookalike = type === drawerType.LOOKALIKE;

  const headerConfig = useSegmentHeaderElements({
    mode: 'expandable',
    expanded,
    isSelected,
    onToggleExpand: () => setExpanded(!expanded),
    onSelect,
  });

  const metadata = useMemo(
    () => createSegmentMetadata(segment, t, selectedAccountId, providerName),
    [segment, t, selectedAccountId, providerName]
  );

  const displayReach = formatReachValue(t, metadata.reach);
  const isPredefinedSegment = isFormSegment(segment) && segment.category === SegmentCategory.PREDEFINED;

  const basicInfoProps = useMemo(() => {
    if (isFormSegment(segment) && !isLookalike) {
      return createBasicInfoPropsFromSegment({
        segment,
        id: metadata.id,
        reach: metadata.reach,
        displayReach,
        category: metadata.category || '',
        organizationName,
        providerName: metadata.providerName,
        isPredefinedSegment,
      });
    }

    return createBasicInfoPropsFromLookalike({
      id: metadata.id,
      reach: metadata.reach,
      displayReach,
      category: metadata.category || '',
      organizationName,
      providerName: metadata.providerName,
      isSelectedList: false,
      lookalikeReachRange: metadata.lookalikeReachRange,
      isSeed: false,
    });
  }, [isLookalike, metadata, displayReach, organizationName, segment, isPredefinedSegment]);

  return (
    <ListItem data-test={dataTest} sx={itemStyles({ isSelected })}>
      <Stack
        className="segment-list-item"
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignContent: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ overflow: 'hidden', minWidth: 0, flexGrow: 1 }}>
          <SegmentHeader
            showPredefinedSegmentBadge={isPredefinedSegment}
            name={metadata.name}
            isLAL={isLookalike}
            searchPhrase={searchPhrase}
            config={headerConfig}
            expirationAlert={
              isFormSegment(segment) && !isLookalike ? (
                <SegmentAlert {...getSegmentExpirationAlertProps(segment)} />
              ) : undefined
            }
          />

          <BasicInfo {...basicInfoProps} />

          {!expanded && isFormSegment(segment) && (
            <Description description={segment.description || ''} searchPhrase={searchPhrase} />
          )}

          {isFormSegment(segment) && (
            <Collapse data-test="segment-detailed-info" in={expanded} timeout="auto" unmountOnExit>
              <DetailedInfo
                description={segment.description ?? ''}
                searchPhrase={searchPhrase}
                type={metadata.type}
                providerName={providerName ?? ''}
                segmentType={segment.segment_type ?? ''}
                segmentCategory={segment.segment_category ?? ''}
                segmentSubCategory={segment.segment_sub_category ?? ''}
                regionCodes={metadata.regionCodes}
                countryCodes={metadata.countryCodes}
                isPredefined={isPredefinedSegment}
                regions={regions}
                countries={countries}
              />
            </Collapse>
          )}
        </Box>

        {headerConfig.rightIcon}
      </Stack>
    </ListItem>
  );
};
